import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { Course } from '@app/app/page-modules/course/models/course.model';
import { LanguageCodeHelper } from '@app/app/shared/helpers/language-code-helper';
import { RestClientService } from '@app/app/shared/services/rest-client.service';
import { ObservableResult } from '@app/app/shared/store';
import { environment } from '@app/environments/environment';
import { TranslocoService } from '@ngneat/transloco';
import { catchError, switchMap } from 'rxjs/operators';
import {
  CourseCreationRequest,
  CourseDataService,
  CourseSectionBulkUpdateItem,
  CourseSectionRequestUnion,
  CourseSectionResponse,
  CourseUpdateRequest,
  CreateCourseResponse,
  UpdateCourseResponse,
} from './data.service';

@Injectable()
export class ApiCourseDataService implements CourseDataService {
  constructor(
    private client: RestClientService,
    private translocoService: TranslocoService,
  ) {}

  getCourse(courseUri: string, languageCode?: string, playlistPath?: string): ObservableResult<Course> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `courses/${courseUri}`);
    const params = playlistPath ? { in: playlistPath } : null;
    const headers = languageCode ? { 'Accept-Language': languageCode } : null;

    return this.client.get<Course>(url, params, headers).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetCourseDetail'))),
    );
  }

  createCourse(request: CourseCreationRequest): ObservableResult<CreateCourseResponse> {
    const requestBody = { ...request, type: 'COURSE' };

    return this.client
      .post<CreateCourseResponse>(
        Location.joinWithSlash(environment.apiRootUrl || '', 'playlists'),
        requestBody,
        undefined,
        request.languageCode ? LanguageCodeHelper.checkAndGetContentLanguageCode(request.languageCode) : null,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError((err) => ObservableResult.ofError(err)),
      );
  }

  updateCourse(courseUid: string, request: CourseUpdateRequest, languageCode?: string): ObservableResult<UpdateCourseResponse> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `playlists/${courseUid}`);
    const headers = languageCode ? LanguageCodeHelper.checkAndGetContentLanguageCode(languageCode) : null;

    return this.client.patch<UpdateCourseResponse>(url, request, {}, headers).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateCourse'))),
    );
  }

  addCourseSection(courseUid: string, request: CourseSectionRequestUnion, languageCode?: string): ObservableResult<CourseSectionResponse> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `courses/${courseUid}/sections`);
    const headers = languageCode ? LanguageCodeHelper.checkAndGetContentLanguageCode(languageCode) : null;

    return this.client.put<CourseSectionResponse>(url, request, {}, headers).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body || {})),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorCreateSection'))),
    );
  }

  updateCourseSection(
    courseUid: string,
    sectionUid: string,
    request: CourseSectionRequestUnion,
    languageCode?: string,
  ): ObservableResult<CourseSectionResponse> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `courses/${courseUid}/sections/${sectionUid}`);
    const headers = languageCode ? LanguageCodeHelper.checkAndGetContentLanguageCode(languageCode) : null;

    return this.client.post<CourseSectionResponse>(url, request, {}, headers).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body || {})),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateSection'))),
    );
  }

  deleteCourseSection(courseUid: string, sectionUid: string): ObservableResult<void> {
    return this.client
      .delete<void>(Location.joinWithSlash(environment.apiRootUrl || '', `courses/${courseUid}/sections/${sectionUid}`))
      .pipe(
        switchMap(() => ObservableResult.ofSuccess()),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorRemovingSection'))),
      );
  }

  bulkUpdateCourseSections(courseUid: string, sections: CourseSectionBulkUpdateItem[]): ObservableResult<void> {
    const requestBody = sections.map((item) => ({
      uid: item.uid,
      ...item.request,
    }));

    return this.client.post<void>(Location.joinWithSlash(environment.apiRootUrl || '', `courses/${courseUid}/sections`), requestBody).pipe(
      switchMap(() => ObservableResult.ofSuccess()),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateSections'))),
    );
  }

  reorderCourseSections(courseUid: string, request: { sections: string[] }): ObservableResult<void> {
    return this.client
      .put<void>(Location.joinWithSlash(environment.apiRootUrl || '', `courses/${courseUid}/sections/reorder`), request)
      .pipe(
        switchMap(() => ObservableResult.ofSuccess()),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorReorderSections'))),
      );
  }

  publishCourse(courseUid: string): ObservableResult<void> {
    return this.client.post<void>(Location.joinWithSlash(environment.apiRootUrl || '', `playlists/${courseUid}/publication`)).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorPublishCourse'))),
    );
  }

  unpublishCourse(courseUid: string): ObservableResult<void> {
    return this.client.delete<void>(Location.joinWithSlash(environment.apiRootUrl || '', `playlists/${courseUid}/publication`)).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUnpublishCourse'))),
    );
  }

  archiveCourse(courseUid: string): ObservableResult<void> {
    return this.client.post<void>(Location.joinWithSlash(environment.apiRootUrl || '', `playlists/${courseUid}/archives`)).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorArchiveCourse'))),
    );
  }
}
